type NumericLike = number | string | null | undefined;

const finiteOrNull = (value: NumericLike): number | null => {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
};

const timedRanges = (value: unknown): Array<Record<string, unknown>> =>
    (Array.isArray(value) ? value : [])
        .map((range: any) => ({
            id: String(range?.id ?? ""),
            startSec: finiteOrNull(range?.startSec),
            endSec: finiteOrNull(range?.endSec),
            atSec: finiteOrNull(range?.atSec),
            durationSec: finiteOrNull(range?.durationSec),
            timeSec: finiteOrNull(range?.timeSec),
            isSceneBoundary: Boolean(range?.isSceneBoundary),
        }))
        .sort((first, second) => JSON.stringify(first).localeCompare(JSON.stringify(second)));

const sourceSegments = (value: unknown): Array<Record<string, unknown>> =>
    (Array.isArray(value) ? value : [])
        .map((segment: any) => ({
            id: String(segment?.id ?? ""),
            offsetSec: finiteOrNull(segment?.offsetSec),
            inSec: finiteOrNull(segment?.range?.inSec),
            outSec: finiteOrNull(segment?.range?.outSec),
            playbackRate: finiteOrNull(segment?.playbackRate),
        }))
        .sort((first, second) => JSON.stringify(first).localeCompare(JSON.stringify(second)));

/**
 * Snapshot the timing model that gives a transcript anchor its meaning.
 *
 * A composition session id only changes when a different composition is
 * opened. This fingerprint also changes for same-composition cuts, inserted
 * time, scene boundaries, speed changes, and source-placement ripples, so an
 * asynchronously prepared insertion cannot land beside different words.
 */
export const getScriptInsertionTargetFingerprint = (scriptClip: any): string => {
    if (!scriptClip) return "";

    let inputConfigs: any[] = [];
    try {
        const configs = scriptClip.getInputConfigs?.();
        inputConfigs = Array.isArray(configs) ? configs : [];
    } catch {}

    let derivedSegments: any[] = [];
    try {
        derivedSegments = Array.isArray(scriptClip.scriptSegments)
            ? scriptClip.scriptSegments
            : [];
    } catch {}

    let sceneRates: Array<Record<string, unknown>> = [];
    try {
        const resolvedScenes = scriptClip.getScenes?.();
        const scenes: any[] = Array.isArray(resolvedScenes) ? resolvedScenes : [];
        sceneRates = scenes.map((scene: any) => ({
            index: finiteOrNull(scene?.index),
            startSec: finiteOrNull(scene?.startSec),
            endSec: finiteOrNull(scene?.endSec),
            playbackRate: finiteOrNull(
                scriptClip.getEffectivePlaybackRateForScene?.(Number(scene?.index ?? 0)),
            ),
        }));
    } catch {}

    return JSON.stringify({
        id: String(scriptClip?.id ?? ""),
        startSec: finiteOrNull(scriptClip?.start?.seconds),
        rangeInSec: finiteOrNull(scriptClip?.range?.[0]?.seconds),
        rangeOutSec: finiteOrNull(scriptClip?.range?.[1]?.seconds),
        durationSec: finiteOrNull(scriptClip?.duration?.seconds),
        timelineDurationSec: finiteOrNull(scriptClip?.timelineDuration?.seconds),
        playbackRate: finiteOrNull(scriptClip?.playbackRate),
        splits: timedRanges(scriptClip?.splits),
        cuts: timedRanges(scriptClip?.scriptCuts),
        gaps: timedRanges(scriptClip?.gaps),
        gapInsertions: timedRanges(scriptClip?.gapInsertions),
        derivedSegments: derivedSegments.map((segment: any) => ({
            srcStartSec: finiteOrNull(segment?.srcStartSec),
            srcEndSec: finiteOrNull(segment?.srcEndSec),
            compStartSec: finiteOrNull(segment?.compStartSec),
            playbackRate: finiteOrNull(segment?.playbackRate),
            isGap: Boolean(segment?.isGap),
        })),
        inputs: inputConfigs
            .map((config: any) => ({
                assetId: String(config?.assetId ?? ""),
                offsetSec: finiteOrNull(config?.offsetSec),
                inSec: finiteOrNull(config?.range?.inSec),
                outSec: finiteOrNull(config?.range?.outSec),
                playbackRate: finiteOrNull(config?.playbackRate),
                segments: sourceSegments(config?.segments),
            }))
            .sort((first, second) =>
                String(first.assetId).localeCompare(String(second.assetId)),
            ),
        sceneRates,
    });
};
